import { useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingBag, Star, Settings, Inbox, LogOut, ExternalLink } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";
import { api } from "@/lib/api";
import AdminProducts from "./AdminProducts";
import AdminOrders from "./AdminOrders";
import AdminReviews from "./AdminReviews";
import AdminSettings from "./AdminSettings";
import AdminInbox from "./AdminInbox";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: Package },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/reviews", label: "Reviews", icon: Star },
  { to: "/admin/inbox", label: "Inbox", icon: Inbox },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

function Dashboard() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    Promise.all([
      api.get("/admin/orders"),
      api.get("/admin/reviews", { params: { status: "pending" } }),
      api.get("/admin/products"),
      api.get("/admin/orders-badge"),
    ]).then(([orders, pending, products, badge]) => {
      const paid = orders.data.filter((o) => o.payment_status === "paid");
      setStats({
        orders: orders.data.length,
        paid: paid.length,
        revenue: paid.reduce((s, o) => s + o.total, 0),
        pendingReviews: pending.data.length,
        products: products.data.length,
        newOrders: badge.data.new_orders,
      });
    }).catch(() => {});
  }, []);

  const cards = stats ? [
    { label: "New Orders", value: stats.newOrders, to: "/admin/orders", testid: "stat-new-orders" },
    { label: "Paid Orders", value: stats.paid, to: "/admin/orders", testid: "stat-paid-orders" },
    { label: "Revenue", value: `$${stats.revenue.toFixed(2)}`, to: "/admin/orders", testid: "stat-revenue" },
    { label: "Pending Reviews", value: stats.pendingReviews, to: "/admin/reviews", testid: "stat-pending-reviews" },
    { label: "Products", value: stats.products, to: "/admin/products", testid: "stat-products" },
  ] : [];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-serif-display text-3xl text-white font-light mb-8">Dashboard</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <button key={c.label} onClick={() => navigate(c.to)} data-testid={c.testid}
            className="gold-hairline bg-[#0D0D0D] p-5 text-left hover:border-[#D4AF37]/50 transition-colors">
            <p className="font-sans-body text-[10px] uppercase tracking-[0.25em] text-[#C6A15B]">{c.label}</p>
            <p className="font-mono-price text-2xl text-white mt-2">{c.value}</p>
          </button>
        ))}
      </div>
      <p className="font-sans-body text-xs text-[#666666] font-light mt-10">
        Manage products, orders, reviews and store settings from the menu. Set your notification
        email under Settings to receive new order alerts.
      </p>
    </div>
  );
}

export default function AdminLayout() {
  const { admin, logout } = useAdmin();
  const [badge, setBadge] = useState(0);

  useEffect(() => {
    if (!admin) return;
    const fetchBadge = () => api.get("/admin/orders-badge").then((r) => setBadge(r.data.new_orders)).catch(() => {});
    fetchBadge();
    const t = setInterval(fetchBadge, 30000);
    return () => clearInterval(t);
  }, [admin]);

  if (admin === null) {
    return <div className="min-h-screen bg-[#080808] flex items-center justify-center font-sans-body text-sm text-[#B8B8B8]">Loading...</div>;
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#080808] flex" data-testid="admin-layout">
      <aside className="w-56 shrink-0 bg-[#0A0A0A] border-r border-[#D4AF37]/15 min-h-screen p-5 hidden md:flex flex-col">
        <p className="font-serif-display gold-text text-lg tracking-[0.25em] px-2 mb-8">KUBWALO</p>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 font-sans-body text-xs uppercase tracking-[0.15em] transition-colors ${
                  isActive ? "text-[#D4AF37] bg-[#D4AF37]/10" : "text-[#B8B8B8] hover:text-white"
                }`
              }>
              <n.icon size={15} strokeWidth={1.5} />
              {n.label}
              {n.label === "Orders" && badge > 0 && (
                <span className="ml-auto bg-[#D4AF37] text-[#080808] text-[9px] font-bold px-1.5 py-0.5 rounded-full" data-testid="admin-orders-badge">
                  {badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <a href="/" className="flex items-center gap-3 px-3 py-2.5 font-sans-body text-xs uppercase tracking-[0.15em] text-[#B8B8B8] hover:text-white" data-testid="admin-view-store">
          <ExternalLink size={15} strokeWidth={1.5} /> View Store
        </a>
        <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 font-sans-body text-xs uppercase tracking-[0.15em] text-[#B8B8B8] hover:text-red-400" data-testid="admin-logout-button">
          <LogOut size={15} strokeWidth={1.5} /> Sign Out
        </button>
      </aside>

      <div className="flex-1 min-w-0">
        <div className="md:hidden flex gap-2 overflow-x-auto p-4 border-b border-[#D4AF37]/15">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-mobile-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-2 font-sans-body text-[11px] uppercase tracking-[0.15em] border ${isActive ? "border-[#D4AF37] text-[#D4AF37]" : "border-[#D4AF37]/20 text-[#B8B8B8]"}`
              }>
              {n.label}{n.label === "Orders" && badge > 0 ? ` (${badge})` : ""}
            </NavLink>
          ))}
          <button onClick={logout} className="whitespace-nowrap px-3 py-2 font-sans-body text-[11px] uppercase tracking-[0.15em] border border-[#D4AF37]/20 text-[#B8B8B8]" data-testid="admin-logout-mobile">
            Sign Out
          </button>
        </div>
        <main className="p-6 lg:p-10">
          <Routes>
            <Route index element={<Dashboard />} />
            <Route path="products" element={<AdminProducts />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="reviews" element={<AdminReviews />} />
            <Route path="inbox" element={<AdminInbox />} />
            <Route path="settings" element={<AdminSettings />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
